import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, User, Award, Clock, Shield, Target, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteDesigner = () => {
  const faqs = [
    {
      question: "What does a landscaping website designer specialize in?",
      answer: "A landscaping website designer creates portfolio-driven websites for landscapers, hardscapers, and outdoor living specialists. They understand how to showcase project work, position high-ticket services, and convert visitors into consultation requests."
    },
    {
      question: "How much does a landscaping website designer charge?",
      answer: "Freelance designers typically charge $2,000–$8,000 for portfolio-focused sites. Agency designers or subscription services range from $200–$500/month. Premium portfolios with complex galleries can cost more."
    },
    {
      question: "Should I hire a freelancer or agency for my landscaping website?",
      answer: "Freelancers offer lower upfront costs but may lack ongoing support. Agencies provide maintenance and updates. For landscaping businesses, portfolio updates are frequent—ongoing support matters."
    },
    {
      question: "What should I look for in a landscaping website designer?",
      answer: "Look for: experience with hardscape/landscaping portfolios, examples of project galleries, understanding of high-ticket service positioning, and SEO knowledge. Ask about mobile optimization and load speeds."
    },
    {
      question: "How long does it take to design a landscaping website?",
      answer: "Freelance designers typically take 3–8 weeks for portfolio sites. We deliver in 10–14 days because of our niche focus and proven templates for landscaping businesses."
    },
    {
      question: "Will the designer help showcase my landscaping projects effectively?",
      answer: "A good landscaping website designer creates before/after galleries, project detail pages, and visual proof that helps close high-ticket sales. Ask to see portfolio examples before hiring."
    },
    {
      question: "Can a designer help me rank in local searches?",
      answer: "Quality designers build SEO into the foundation—service area pages, proper headings, schema markup. But ranking also depends on content quality and ongoing optimization."
    },
    {
      question: "What do I need to provide to the designer?",
      answer: "High-quality project photos (before/after preferred), your service list, service areas, and any testimonials. Good designers handle copywriting, layout, and SEO structure."
    },
    {
      question: "What happens after my landscaping website launches?",
      answer: "With freelancers, support often ends. With subscription models, you get ongoing maintenance, portfolio updates, and seasonal changes handled. Landscaping businesses need frequent updates."
    },
    {
      question: "How do I know if a designer is right for my landscaping business?",
      answer: "Ask for landscaping-specific examples—patios, retaining walls, outdoor kitchens. If they can't show relevant work, they may not understand how to position your services effectively."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-designer/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Landscaping website designers specializing in portfolio websites for landscapers and hardscapers."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-designer/#webpage",
    "name": "Landscaping Website Designer | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-designer/",
    "description": "Find a landscaping website designer who builds portfolio-driven sites. We design websites exclusively for landscapers. $0 upfront.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Landscaping Website Designer | Leaf Rush"
        description="Find a landscaping website designer who builds portfolio-driven sites. We design websites exclusively for landscapers. $0 upfront."
        keywords="landscaping website designer, landscaper web designer, website designer for landscapers, hardscape website designer"
        canonical="https://www.leafrushmarketing.com/landscaping-website-designer/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-designer/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Portfolio-Focused Designer
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Designer Who Showcases Your Best Work
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Looking for a landscaping website designer? We design portfolio-driven websites for hardscapers, landscape designers, and outdoor living specialists.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    A landscaping website designer creates portfolio-driven sites that showcase hardscape and landscape projects. Freelancers charge $2,000–$8,000; agencies charge $200–$500/month. Look for portfolio experience and SEO knowledge.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're exploring all your options, visit our main{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                page for a complete overview.
              </div>
            </div>
          </div>
        </section>

        {/* What to Look For */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What to Look for in a Landscaping Website Designer
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Portfolio Expertise</p>
                    <p className="text-sm text-muted-foreground">
                      They should know how to showcase before/after projects that close high-ticket sales.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Hardscape Knowledge</p>
                    <p className="text-sm text-muted-foreground">
                      Patios, retaining walls, outdoor kitchens—they should understand how to position each service.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Award className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">High-Ticket Focus</p>
                    <p className="text-sm text-muted-foreground">
                      Your site should attract $10K–$100K+ projects, not just small maintenance work.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Ongoing Support</p>
                    <p className="text-sm text-muted-foreground">
                      Landscaping portfolios need frequent updates. Make sure support is included.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Designer vs Agency */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Freelance Designer vs. Agency: Which is Right?
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Factor</th>
                      <th className="text-left py-3 px-4">Freelance Designer</th>
                      <th className="text-left py-3 px-4">Niche Agency (Us)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Cost</td>
                      <td className="py-3 px-4">$2,000–$8,000 one-time</td>
                      <td className="py-3 px-4">$200–$300/mo, $0 upfront</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Timeline</td>
                      <td className="py-3 px-4">3–8 weeks</td>
                      <td className="py-3 px-4">10–14 days</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Portfolio Updates</td>
                      <td className="py-3 px-4">Extra cost per update</td>
                      <td className="py-3 px-4">Included in subscription</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Industry Focus</td>
                      <td className="py-3 px-4">Usually generalist</td>
                      <td className="py-3 px-4">Landscaping/hardscape only</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">SEO Included</td>
                      <td className="py-3 px-4">Variable</td>
                      <td className="py-3 px-4">Built into every site</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full cost breakdown:{" "}
                <NavLink to="/landscaping-website-cost/" className="text-primary underline hover:no-underline">
                  landscaping website cost
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* What We Design */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                How We Design Landscaping Websites
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Galleries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Before/after photos, project details, and visual proof that helps close high-ticket sales.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Service Pages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Patios, retaining walls, outdoor kitchens, plantings—each service gets its own optimized page.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Local SEO</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Service area pages, schema markup, and on-page optimization to rank where you work.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  See{" "}
                  <NavLink to="/landscaping-website-examples/" className="text-primary underline hover:no-underline">
                    landscaping website examples
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/landscaping-portfolio-website/" className="text-primary underline hover:no-underline">
                    landscaping portfolio website guide
                  </NavLink>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Landscaping Website Designers
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/landscaping-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional landscaping websites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-content/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Content</h3>
                  <p className="text-sm text-muted-foreground">What content you need for your site.</p>
                </NavLink>
                <NavLink to="/landscaping-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for landscaping sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready to Hire a Landscaping Website Designer?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Let's discuss your landscaping business and see if we're a fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/contact#get-started">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </NavLink>
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteDesigner;
